import { useEffect, useLayoutEffect, useRef } from 'react'
import { EventData, EventDataMap, EventMapItem } from 'types/websocket.types'

export const useWebsocket = <T extends keyof EventDataMap>(
    eventMapItems: EventMapItem<T>[]
) => {
    const ws = useRef<WebSocket | null>(null)
    const eventMapItemsRef = useRef(eventMapItems)

    // Refresh the ref after each render.
    useLayoutEffect(() => {
        eventMapItemsRef.current = eventMapItems
    }, [eventMapItems])

    useEffect(() => {
        if (!ws.current) {
            ws.current = new WebSocket(
                process.env.REACT_APP_WEBSOCKET_URL_V3 || ''
            )
        }

        const handleMessage = (event: MessageEvent) => {
            const wsEvent = JSON.parse(event.data) as EventData

            // Narrowing the type of wsEvent based on its "type"
            const isEventType = (
                event: EventData,
                type: T
            ): event is EventDataMap[T] => event.type === type

            const eventMapItems = eventMapItemsRef.current ?? []

            eventMapItems.forEach(({ events, callback }) => {
                const eventsArray = Array.isArray(events) ? events : [events]
                if (
                    eventsArray.includes(wsEvent.type as T) &&
                    isEventType(wsEvent, wsEvent.type as T)
                ) {
                    callback(wsEvent) // TypeScript now knows wsEvent matches EventDataMap[T]
                }
            })
        }

        ws.current.addEventListener('message', handleMessage)

        return () => {
            ws.current?.removeEventListener('message', handleMessage)
        }
    }, [])
}
